import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {useDropzone} from "react-dropzone"
import {extractText, getDocumentProxy} from "unpdf"
import dropzoneImg from "../../assets/img/dropzoneImg.svg"
import taskIcon from "../../assets/icon/task.svg"
import GetFirstSegment from "../../tools/GetFirstSegment"
import mammoth from "mammoth"
import JSZip from "jszip"

export default function Dropzone({
    type,
    setUploadFile,
    maxSize,
    maxDuration,
    maxCharacters,
    transcriptionStatus,
    generationStatus,
    fileName,
    uploadStatus,
    fileType,
    setFileType,
    pdf
    }) {
    const {t} = useTranslation()
    const [isFileError, setIsFileError] = useState(null)
    const [isFileValid, setIsFileValid] = useState(null)
    const {acceptedFiles, getRootProps, getInputProps} = useDropzone({
        disabled: (transcriptionStatus === "done" || generationStatus === "done"),
        accept: {"text/plain": [".txt", ".srt"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx", ".otd"],
            ...(pdf && {"application/pdf": [".pdf"]}),
            "video/*": [".mp4", ".mkv"],
            "audio/*": [".mp3", ".wav", ".ogg", ".flac", ".m4a"]
        },
    })

    const getVideoDuration = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement("video")
            video.preload = "metadata"
            video.onloadedmetadata = () => {
                resolve(video.duration)
            }
            video.onerror = () => {
                reject("Unable to load video metadata.")
            }
            video.src = URL.createObjectURL(file)
        })
    }

    useEffect(() => {
        const file = acceptedFiles[0]
        if (!file) return
        setIsFileError(null)
        setUploadFile(null)
        setIsFileValid(null)
        const extension = file.name.split('.').pop().toLowerCase()

        if (file.size > maxSize) {
            setIsFileError(t("File is too large! Max size 500 MB"))
            return
        }

        const handleTextContent = (textContent) => {
            if (textContent.length > maxCharacters) {
                setIsFileError(t("File content is too long! Max ") + maxCharacters + t(" characters"))
            } else {
                setIsFileValid(file)
                setUploadFile(file)
            }
        }

        if (["txt", "srt"].includes(extension)) {
            const reader = new FileReader()
            reader.onload = (e) => handleTextContent(e.target.result)
            reader.readAsText(file)
            return
        }
        if (["pdf"].includes(extension)) {
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const arrayBuffer = e.target.result
                    const pdf = await getDocumentProxy(arrayBuffer)
                    const { text } = await extractText(pdf, { mergePages: true })
                    handleTextContent(text)
                } catch {
                    setIsFileError(t("Could not read PDF file"))
                }
            }
            reader.readAsArrayBuffer(file)
            return
        }
        if (extension === "docx") {
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const result = await mammoth.extractRawText({arrayBuffer: e.target.result})
                    handleTextContent(result.value)
                } catch {
                    setIsFileError(t("Could not read DOCX file"))
                }
            }
            reader.readAsArrayBuffer(file)
            return
        }
        if (extension === "odt") {
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const zip = await JSZip.loadAsync(e.target.result)
                    const contentXml = await zip.file("content.xml")?.async("string")
                    if (!contentXml) throw new Error()
                    const parser = new DOMParser()
                    const xmlDoc = parser.parseFromString(contentXml, "text/xml")
                    const paragraphs = Array.from(xmlDoc.getElementsByTagName("text:p"))
                    const textContent = paragraphs.map(p => p.textContent).join("\n")
                    handleTextContent(textContent)
                } catch {
                    setIsFileError(t("Could not read ODT file"))
                }
            }
            reader.readAsArrayBuffer(file)
            return
        }
        if (file.type.startsWith("video") || file.type.startsWith("audio")) {
            getVideoDuration(file)
                .then((duration) => {
                    if (duration > maxDuration) {
                        setIsFileError(t("Media duration exceeds allowed limit! Max ") + `${maxDuration / 60} minutes`)
                    } else {
                        setIsFileValid(file)
                        setUploadFile(file)
                    }
                })
                .catch(() => {
                    setIsFileError(t("Could not read media duration."))
                })
            try {
                setFileType(GetFirstSegment(file.type))
            } catch {}
            return
        }
    }, [acceptedFiles])

    return (
        <>
        <section className={(transcriptionStatus || generationStatus) === "done" ? "min-dropzone-disabled"
            : (uploadStatus === "done" && fileType === "video") ? "min-dropzone": "dropzone"} title={t("Select or drag and drop your media")}>
            <div {...getRootProps({className: "content"})}>
                <input {...getInputProps()}/>
                {isFileError ?
                    <>
                    <img src={dropzoneImg} alt=""/>
                    <p>{isFileError}</p>
                    </>
                 : isFileValid ?
                    <>
                    <img src={taskIcon} alt=""/>
                    <span>{isFileValid.name}</span>
                    </>
                 :
                    <>
                    <img src={dropzoneImg} alt=""/>
                    {fileName ?
                        <><p className="filename-title">{fileName}</p></>
                     :
                        <>
                        <p>{type === "st" ? t("Upload video or audio") : t("Upload text, video or audio")}</p>
                        <span>{t("Max size 500 MB")} | {t("Max duration ") + `${maxDuration / 60} minutes`}</span>
                        </>
                    }
                    </>
                }
            </div>
        </section>
        </>
    )
}